import React, {
  useRef,
  useState,
  useEffect,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  SetStateAction,
  Dispatch,
} from 'react';
import 'react-multi-email/dist/style.css';
import { ReactMultiEmail } from 'react-multi-email';
import {
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';

import Input from '../../../../Common/InputComponents/Input';
import Select, { SelectRef } from '../../../../Common/InputComponents/Select';

import { AlarmMetadata, AlarmDefinition, EmailRecipients } from 'types/types';
import { OutputValidationError } from '../CreateForm';

interface OutputElementsProps {
  alarmMetaData?: AlarmMetadata;
  selectedNotificationType: string;
  alarmDefinitionData: AlarmDefinition | null;
  setSelectedNotificationType: Dispatch<React.SetStateAction<string>>;
}
export interface OutputElementsRef {
  recipients: EmailRecipients;
  subject: RefObject<HTMLInputElement>;
  notificationType: RefObject<SelectRef>;
  recipientContainer: RefObject<HTMLDivElement>;
  outputValidation: Dispatch<SetStateAction<OutputValidationError>>;
}

export enum NotificationType {
  EMAIL = 'Email',
}

const OutputElements: ForwardRefRenderFunction<
  OutputElementsRef,
  OutputElementsProps
> = (
  {
    alarmMetaData,
    alarmDefinitionData,
    selectedNotificationType,
    setSelectedNotificationType,
  },
  ref
) => {
  const initialErrors: OutputValidationError = {
    recipients: '',
    subject: '',
  };
  const subjectRef = useRef<HTMLInputElement>(null);
  const recipientsRef = useRef<HTMLDivElement>(null);
  const notificationTypeRef = useRef<SelectRef>(null);

  const [recepientsEmails, setRecepientsEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] =
    useState<OutputValidationError>(initialErrors);

  const handleOnChange = (field: string) => {
    switch (field.toUpperCase()) {
      case 'SUBJECT':
        if (validationErrors.subject !== '')
          setValidationErrors((prev) => {
            return { ...prev, subject: '' };
          });
        break;
      case 'RECIPIENTS':
        if (validationErrors.recipients !== '')
          setValidationErrors((prev) => {
            return { ...prev, recipients: '' };
          });
        break;
    }
  };

  const handleOnNotificationTypeChange = (value?: string) => {
    setSelectedNotificationType(value ?? '');
  };

  useImperativeHandle(ref, () => ({
    notificationType: notificationTypeRef,
    recipients: { to: recepientsEmails, cc: ccEmails },
    recipientContainer: recipientsRef,
    subject: subjectRef,
    outputValidation: setValidationErrors,
  }));

  useEffect(() => {
    if (alarmDefinitionData?.output_serialized?.recipients) {
      setRecepientsEmails(
        alarmDefinitionData?.output_serialized?.recipients.to
      );
      setCcEmails(alarmDefinitionData?.output_serialized?.recipients.cc);
    }
    if (alarmDefinitionData?.output_serialized?.notificationType) {
      setSelectedNotificationType(
        alarmDefinitionData?.output_serialized?.notificationType
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmDefinitionData]);

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Typography className="form-element-label">
            Notification Type
          </Typography>
          <Select
            size="small"
            id="notificationType"
            className="input-elements"
            inputRef={notificationTypeRef}
            handleOnChange={handleOnNotificationTypeChange}
            value={
              alarmDefinitionData?.output_serialized?.notificationType ?? ''
            }
          >
            <MenuItem value="">None</MenuItem>
            {alarmMetaData?.notificationType?.map((s) => (
              <MenuItem key={`notificationType-${s}`} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        {selectedNotificationType === NotificationType.EMAIL && (
          <Grid item xs={5}>
            <Typography className="form-element-label">Subject</Typography>
            <Input
              handleOnChange={() => handleOnChange('subject')}
              size="small"
              type="text"
              id="subject"
              inputRef={subjectRef}
              error={!!validationErrors.subject}
              helperText={validationErrors.subject}
              className="input-elements"
              value={alarmDefinitionData?.output_serialized?.subject ?? ''}
            />
          </Grid>
        )}
      </Grid>
      {selectedNotificationType === NotificationType.EMAIL && (
        <React.Fragment>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <FormControl fullWidth error={!!validationErrors.recipients}>
                <Typography className="form-element-label-email">
                  To:
                </Typography>
                <div
                  ref={recipientsRef}
                  className="email-container"
                  style={{
                    borderColor: validationErrors.recipients ? '#d32f2f' : '',
                  }}
                >
                  <ReactMultiEmail
                    style={{
                      border: 'none',
                    }}
                    placeholder="Add email"
                    emails={recepientsEmails}
                    onChange={(_emails: string[]) => {
                      setRecepientsEmails(_emails);
                      handleOnChange('recipients');
                    }}
                    getLabel={(email, index, removeEmail) => {
                      return (
                        <div
                          data-tag
                          key={index}
                          style={{
                            fontSize: '0.8rem',
                          }}
                        >
                          <div data-tag-item>{email}</div>
                          <span
                            data-tag-handle
                            onClick={() => removeEmail(index)}
                            style={{
                              cursor: 'pointer',
                            }}
                          >
                            ×
                          </span>
                        </div>
                      );
                    }}
                  />
                </div>
                <FormHelperText>{validationErrors.recipients}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Typography className="form-element-label-email">CC:</Typography>
              <div className="email-container">
                <ReactMultiEmail
                  style={{
                    border: 'none',
                  }}
                  placeholder="Add email"
                  emails={ccEmails}
                  onChange={(_emails: string[]) => {
                    setCcEmails(_emails);
                  }}
                  getLabel={(email, index, removeEmail) => {
                    return (
                      <div
                        data-tag
                        key={index}
                        style={{
                          fontSize: '0.8rem',
                        }}
                      >
                        <div data-tag-item>{email}</div>
                        <span
                          data-tag-handle
                          onClick={() => removeEmail(index)}
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          ×
                        </span>
                      </div>
                    );
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default forwardRef<OutputElementsRef, OutputElementsProps>(
  OutputElements
);
